import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { useCasting } from "@/context/CastingContext";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";

const TYPES = [
  "Model Booking Enquiry",
  "Actor Casting Enquiry",
  "Voice Casting Enquiry",
  "Campaign & Production Enquiry",
  "Request a Curated Shortlist",
];
const TYPE_LABELS_FR = {
  "Model Booking Enquiry": "Réservation de modèle",
  "Actor Casting Enquiry": "Casting d'acteur",
  "Voice Casting Enquiry": "Casting vocal",
  "Campaign & Production Enquiry": "Campagne et production",
  "Request a Curated Shortlist": "Présélection sur mesure",
};
const BUDGETS = ["Under $10k", "$10k – $50k", "$50k – $150k", "$150k+", "To be discussed"];
const BUDGET_LABELS_FR = {
  "Under $10k": "Moins de 10 k$",
  "$10k – $50k": "10 k$ – 50 k$",
  "$50k – $150k": "50 k$ – 150 k$",
  "$150k+": "150 k$ +",
};

const initial = { enquiry_type: TYPES[0], client_name: "", company: "", email: "", project: "", dates: "", location: "", usage: "", budget: "", brief: "" };

const inputCls =
  "w-full border border-white/15 bg-black/40 px-4 py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-gold";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">{label}</span>
    {children}
  </label>
);

export default function Clients() {
  const { t, lang } = useLanguage();
  useSEO(t("clients.eyebrow") + " — Diamotims Agency", t("clients.intro"));
  const { board, remove, clear } = useCasting();
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const honeypotRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const typeLabel = (v) => (lang === "fr" ? TYPE_LABELS_FR[v] ?? v : v);
  const budgetLabel = (v) => (lang === "fr" ? BUDGET_LABELS_FR[v] ?? t("clients.budgetTbd") : v);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", {
        ...form,
        shortlist: board.map((x) => x.name),
        website: honeypotRef.current?.value || "",
      });
      setDone(true);
      setForm(initial);
      clear();
      toast.success(t("clients.successTitle"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="clients-page" className="pt-28 lg:pt-36">
      <div className="mx-auto max-w-[1800px] px-4 pb-24 sm:px-8 lg:px-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold sm:text-xs">{t("clients.eyebrow")}</p>
        <h1 className="mt-4 max-w-5xl font-display text-5xl font-light uppercase leading-[0.95] tracking-tight text-ivory sm:text-7xl">
          <MaskedLine delay={0.1}>{t("clients.title1")}</MaskedLine>
          <MaskedLine delay={0.22}>
            <span className="italic text-gold">{t("clients.title2")}</span>
          </MaskedLine>
        </h1>
        <FadeUp delay={0.35}>
          <p className="mt-8 max-w-2xl font-display text-xl font-light italic leading-relaxed text-white/75 sm:text-2xl">
            {t("clients.intro")}
          </p>
        </FadeUp>

        <FadeUp delay={0.2} className="mt-14 max-w-3xl">
          {done ? (
            <div data-testid="brief-success-message" className="flex min-h-[420px] flex-col items-center justify-center border border-gold/40 bg-[#121212] p-10 text-center">
              <div className="flex h-14 w-14 items-center justify-center border border-gold text-gold">
                <Check className="h-6 w-6" />
              </div>
              <h2 className="mt-6 font-display text-3xl font-light uppercase tracking-tight text-white">{t("clients.successTitle")}</h2>
              <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/60">{t("clients.successBody")}</p>
              <button
                data-testid="brief-again-button"
                onClick={() => setDone(false)}
                className="mt-8 border border-white/30 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.3em] text-white transition-colors hover:border-gold hover:text-gold"
              >
                {t("clients.again")}
              </button>
            </div>
          ) : (
            <form data-testid="casting-brief-form" onSubmit={submit} className="border border-white/10 bg-[#121212] p-6 sm:p-10">
              <input ref={honeypotRef} type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Field label={t("clients.type")}>
                    <select data-testid="brief-select-type" value={form.enquiry_type} onChange={set("enquiry_type")} className={`${inputCls} appearance-none`}>
                      {TYPES.map((v) => (
                        <option key={v} value={v} className="bg-[#121212]">{typeLabel(v)}</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label={t("clients.name")}>
                  <input data-testid="brief-input-client-name" required minLength={2} value={form.client_name} onChange={set("client_name")} className={inputCls} />
                </Field>
                <Field label={t("clients.company")}>
                  <input data-testid="brief-input-company" value={form.company} onChange={set("company")} className={inputCls} />
                </Field>
                <Field label={t("clients.email")}>
                  <input data-testid="brief-input-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} />
                </Field>
                <Field label={t("clients.project")}>
                  <input data-testid="brief-input-project" value={form.project} onChange={set("project")} className={inputCls} />
                </Field>
                <Field label={t("clients.dates")}>
                  <input data-testid="brief-input-dates" value={form.dates} onChange={set("dates")} className={inputCls} />
                </Field>
                <Field label={t("clients.location")}>
                  <input data-testid="brief-input-location" value={form.location} onChange={set("location")} className={inputCls} />
                </Field>
                <Field label={t("clients.usage")}>
                  <input data-testid="brief-input-usage" value={form.usage} onChange={set("usage")} className={inputCls} />
                </Field>
                <Field label={t("clients.budget")}>
                  <select data-testid="brief-select-budget" value={form.budget} onChange={set("budget")} className={`${inputCls} appearance-none`}>
                    <option value="" className="bg-[#121212]">{t("clients.budgetSelect")}</option>
                    {BUDGETS.map((b) => (
                      <option key={b} value={b} className="bg-[#121212]">{budgetLabel(b)}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="mt-6">
                <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">{t("clients.shortlistLabel")}</span>
                {board.length === 0 ? (
                  <p data-testid="brief-shortlist-empty" className="border border-dashed border-white/15 px-4 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
                    {t("clients.shortlistEmpty")}
                  </p>
                ) : (
                  <div data-testid="brief-shortlist-chips" className="flex flex-wrap gap-2">
                    {board.map((x) => (
                      <span key={x.id} className="flex items-center gap-2 border border-gold/40 bg-black/40 py-1.5 pl-1.5 pr-2">
                        <img src={x.image_url} alt="" className="h-7 w-6 object-cover object-top" />
                        <span className="font-mono text-[10px] uppercase tracking-widest text-white">{x.name}</span>
                        <button type="button" data-testid={`brief-remove-${x.id}`} aria-label={`${t("casting.remove")} ${x.name}`} onClick={() => remove(x.id)}>
                          <X className="h-3 w-3 text-white/50 transition-colors hover:text-gold" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6">
                <Field label={t("clients.brief")}>
                  <textarea data-testid="brief-input-brief" rows={4} value={form.brief} onChange={set("brief")} placeholder={t("clients.briefPh")} className={`${inputCls} resize-none`} />
                </Field>
              </div>

              <p className="mt-6 text-[11px] leading-relaxed text-white/40">{t("clients.privacyNote")}</p>

              <button
                data-testid="casting-brief-submit-button"
                type="submit"
                disabled={busy}
                className="group mt-8 flex w-full items-center justify-center gap-3 bg-ivory px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-black transition-colors duration-300 hover:bg-gold disabled:opacity-50"
              >
                {busy ? t("clients.sending") : t("clients.submit")}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </button>
            </form>
          )}
        </FadeUp>
      </div>
    </main>
  );
}
